import { z } from "zod";

export const AiPolicyVersionV1Schema = z.literal(1);
export type AiPolicyVersionV1 = z.infer<typeof AiPolicyVersionV1Schema>;

export const BusinessGoalV1Schema = z.enum([
  "grow_sales",
  "improve_efficiency",
  "launch_product",
  "clear_inventory",
  "explore_and_learn",
  "defend_branded_traffic",
]);
export type BusinessGoalV1 = z.infer<typeof BusinessGoalV1Schema>;

export const ApprovalRiskThresholdV1Schema = z.enum([
  "all",
  "medium_plus",
  "high_plus",
  "critical_only",
  "none",
]);
export type ApprovalRiskThresholdV1 = z.infer<typeof ApprovalRiskThresholdV1Schema>;

export const RiskPreferenceV1Schema = z.enum(["conservative", "balanced", "aggressive"]);
export type RiskPreferenceV1 = z.infer<typeof RiskPreferenceV1Schema>;

export const AiPolicySourceV1Schema = z.enum(["system", "campaign", "default"]);
export type AiPolicySourceV1 = z.infer<typeof AiPolicySourceV1Schema>;

export const BidChangeRiskThresholdsV1Schema = z
  .object({
    medium: z.number().finite().positive(),
    high: z.number().finite().positive(),
    critical: z.number().finite().positive(),
    weeklyCritical: z.number().finite().positive(),
  })
  .strict()
  .superRefine((thresholds, context) => {
    if (thresholds.medium >= thresholds.high) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["high"],
        message: "high must be greater than medium",
      });
    }
    if (thresholds.high >= thresholds.critical) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["critical"],
        message: "critical must be greater than high",
      });
    }
  });
export type BidChangeRiskThresholdsV1 = z.infer<typeof BidChangeRiskThresholdsV1Schema>;

export const AiPolicyFieldsV1Schema = z
  .object({
    businessGoal: BusinessGoalV1Schema,
    targetAcos: z.number().finite().min(0.01).max(2),
    approvalRiskThreshold: ApprovalRiskThresholdV1Schema,
    riskPreference: RiskPreferenceV1Schema,
    bidChangeRiskThresholds: BidChangeRiskThresholdsV1Schema,
    dryRun: z.boolean(),
  })
  .strict();
export type AiPolicyFieldsV1 = z.infer<typeof AiPolicyFieldsV1Schema>;

/** Product-facing modes are a projection of the lower-level runtime policy fields. */
export const ExecutionModeV1Schema = z.enum(["recommendation", "semi-auto", "full-auto"]);
export type ExecutionModeV1 = z.infer<typeof ExecutionModeV1Schema>;

export function executionModePolicyFieldsV1(
  mode: ExecutionModeV1,
): Pick<AiPolicyFieldsV1, "dryRun" | "approvalRiskThreshold"> {
  switch (mode) {
    case "recommendation":
      return { dryRun: true, approvalRiskThreshold: "all" };
    case "semi-auto":
      return { dryRun: false, approvalRiskThreshold: "medium_plus" };
    case "full-auto":
      return { dryRun: false, approvalRiskThreshold: "critical_only" };
  }
}

export function resolveExecutionModeV1(
  policy: Pick<AiPolicyFieldsV1, "dryRun" | "approvalRiskThreshold">,
): ExecutionModeV1 {
  if (policy.dryRun) return "recommendation";
  return policy.approvalRiskThreshold === "critical_only" || policy.approvalRiskThreshold === "none"
    ? "full-auto"
    : "semi-auto";
}

export function resolveExecutionModePolicyUpdateV1(
  current: Pick<AiPolicyFieldsV1, "dryRun" | "approvalRiskThreshold">,
  selectedMode: ExecutionModeV1,
  selectionChanged: boolean,
): Pick<AiPolicyFieldsV1, "dryRun" | "approvalRiskThreshold"> {
  return selectionChanged
    ? executionModePolicyFieldsV1(selectedMode)
    : {
        dryRun: current.dryRun,
        approvalRiskThreshold: current.approvalRiskThreshold,
      };
}

export const SystemAiPolicyV1Schema = AiPolicyFieldsV1Schema.extend({
  version: AiPolicyVersionV1Schema,
  systemEnabled: z.boolean(),
}).strict();
export type SystemAiPolicyV1 = z.infer<typeof SystemAiPolicyV1Schema>;

export function campaignLeafOverrideV1Schema<T extends z.ZodTypeAny>(valueSchema: T) {
  return z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("follow_system") }).strict(),
    z.object({ mode: z.literal("override"), value: valueSchema }).strict(),
  ]);
}

export type CampaignLeafOverrideV1<T> = { mode: "follow_system" } | { mode: "override"; value: T };

export const CampaignAiPolicyOverrideV1Schema = z
  .object({
    businessGoal: campaignLeafOverrideV1Schema(BusinessGoalV1Schema),
    targetAcos: campaignLeafOverrideV1Schema(z.number().finite().min(0.01).max(2)),
    approvalRiskThreshold: campaignLeafOverrideV1Schema(ApprovalRiskThresholdV1Schema),
    riskPreference: campaignLeafOverrideV1Schema(RiskPreferenceV1Schema),
    bidChangeRiskThresholds: campaignLeafOverrideV1Schema(BidChangeRiskThresholdsV1Schema),
    dryRun: campaignLeafOverrideV1Schema(z.boolean()),
  })
  .strict();
export type CampaignAiPolicyOverrideV1 = z.infer<typeof CampaignAiPolicyOverrideV1Schema>;

/** Editable source versions evolve independently of immutable effective snapshots. */
export const SystemAiPolicyDocumentV1Schema = z
  .object({
    version: AiPolicyVersionV1Schema,
    policy: AiPolicyFieldsV1Schema,
  })
  .strict();
export type SystemAiPolicyDocumentV1 = z.infer<typeof SystemAiPolicyDocumentV1Schema>;

export const CampaignAiPolicyDocumentV1Schema = z
  .object({
    version: AiPolicyVersionV1Schema,
    overrides: CampaignAiPolicyOverrideV1Schema,
  })
  .strict();
export type CampaignAiPolicyDocumentV1 = z.infer<typeof CampaignAiPolicyDocumentV1Schema>;

// Managed Groups continue to inherit the complete bid-risk threshold group from System.
export const ManagedGroupAiPolicyDocumentV1Schema = z
  .object({
    version: AiPolicyVersionV1Schema,
    policy: AiPolicyFieldsV1Schema.omit({ bidChangeRiskThresholds: true }),
    // A confirmed creation opts in only this group's campaigns, not the Workspace.
    enrollmentAuthorization: z
      .object({
        source: z.literal("smart_creation"),
        planVersionId: z.string().uuid(),
        workspaceSuspended: z.boolean().optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type ManagedGroupAiPolicyDocumentV1 = z.infer<typeof ManagedGroupAiPolicyDocumentV1Schema>;

/** Task authorization is a new document contract; v1 evidence remains immutable. */
export const ManagedGroupAiPolicyDocumentV2Schema = z
  .object({
    version: z.literal(2),
    policy: AiPolicyFieldsV1Schema.omit({ bidChangeRiskThresholds: true }),
    enrollmentAuthorization: z
      .object({
        source: z.literal("smart_creation"),
        creationTaskId: z.string().uuid(),
        snapshotHash: z.string().regex(/^[a-f0-9]{64}$/),
        workspaceSuspended: z.boolean().optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type ManagedGroupAiPolicyDocumentV2 = z.infer<typeof ManagedGroupAiPolicyDocumentV2Schema>;
export const ManagedGroupAiPolicyDocumentSchema = z.discriminatedUnion("version", [
  ManagedGroupAiPolicyDocumentV1Schema,
  ManagedGroupAiPolicyDocumentV2Schema,
]);
export type ManagedGroupAiPolicyDocument = z.infer<typeof ManagedGroupAiPolicyDocumentSchema>;

export const AiPolicyEnrollmentV1Schema = z
  .object({
    systemEnabled: z.boolean(),
    campaignEnabled: z.boolean(),
    effectiveEnabled: z.boolean(),
  })
  .strict()
  .superRefine((enrollment, context) => {
    if (enrollment.effectiveEnabled !== (enrollment.systemEnabled && enrollment.campaignEnabled)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["effectiveEnabled"],
        message: "effectiveEnabled must equal systemEnabled && campaignEnabled",
      });
    }
  });
export type AiPolicyEnrollmentV1 = z.infer<typeof AiPolicyEnrollmentV1Schema>;

export const AiPolicySourceMapV1Schema = z
  .object({
    enrollment: z
      .object({
        systemEnabled: z.enum(["system", "default"]),
        campaignEnabled: z.literal("campaign"),
      })
      .strict(),
    businessGoal: AiPolicySourceV1Schema,
    targetAcos: AiPolicySourceV1Schema,
    approvalRiskThreshold: AiPolicySourceV1Schema,
    riskPreference: AiPolicySourceV1Schema,
    bidChangeRiskThresholds: AiPolicySourceV1Schema,
    dryRun: AiPolicySourceV1Schema,
  })
  .strict();
export type AiPolicySourceMapV1 = z.infer<typeof AiPolicySourceMapV1Schema>;

export const AiPolicyDecisionV1Schema = z
  .object({
    version: AiPolicyVersionV1Schema,
    targetPolicyKind: z.enum(["keyword", "auto", "unsupported"]),
    effectiveThreshold: ApprovalRiskThresholdV1Schema,
    dryRun: z.boolean(),
    sourceSummary: AiPolicySourceMapV1Schema,
    risk: z.enum(["low", "medium", "high", "critical"]),
    proposalRisk: z.enum(["low", "medium", "high", "critical"]).nullable(),
    validatorVerdict: z.enum(["executable", "recommendation", "blocked", "unsupported"]),
    reasonCodes: z.array(z.string().min(1).max(100)).max(20),
  })
  .strict();
export type AiPolicyDecisionV1 = z.infer<typeof AiPolicyDecisionV1Schema>;

export const EffectiveAiPolicyV1Schema = AiPolicyFieldsV1Schema.extend({
  version: AiPolicyVersionV1Schema,
  enrollment: AiPolicyEnrollmentV1Schema,
  sources: AiPolicySourceMapV1Schema,
}).strict();
export type EffectiveAiPolicyV1 = z.infer<typeof EffectiveAiPolicyV1Schema>;

export const AiPolicyManagementV2Schema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("direct") }).strict(),
  z
    .object({
      kind: z.literal("managed_group"),
      groupId: z.string().uuid(),
      groupName: z.string().min(1),
      groupVersion: z.number().int().positive(),
      assignmentId: z.string().uuid(),
    })
    .strict(),
]);
export type AiPolicyManagementV2 = z.infer<typeof AiPolicyManagementV2Schema>;

export const AiPolicyBlockedReasonV2Schema = z.enum([
  "creation_not_completed",
  "workspace_ai_disabled",
  "managed_group_not_active",
  "campaign_ai_disabled",
  "campaign_not_enabled",
  "unsupported_campaign_type",
]);
export type AiPolicyBlockedReasonV2 = z.infer<typeof AiPolicyBlockedReasonV2Schema>;

export const AiPolicyRunGatesV2Schema = z
  .object({
    workspaceAiEnabled: z.boolean(),
    managedGroupLifecycleStatus: z.enum(["active", "paused", "archived"]).nullable(),
    campaignAiEnabled: z.boolean(),
    campaignDeliveryStatus: z.enum(["enabled", "paused", "archived"]),
    supportedCampaignType: z.boolean(),
    canStartRound: z.boolean(),
    blockedReasons: z.array(AiPolicyBlockedReasonV2Schema),
  })
  .strict();
export type AiPolicyRunGatesV2 = z.infer<typeof AiPolicyRunGatesV2Schema>;

export const AiPolicySourceV2Schema = z.enum(["system", "campaign", "managed_group", "default"]);
export const AiPolicySourceMapV2Schema = z
  .object({
    enrollment: z
      .object({
        workspaceAiEnabled: z.enum(["system", "default"]),
        managedGroupEnabled: z.literal("managed_group").nullable(),
        campaignAiEnabled: z.literal("campaign"),
        campaignDeliveryStatus: z.literal("campaign"),
        supportedCampaignType: z.literal("campaign"),
      })
      .strict(),
    businessGoal: AiPolicySourceV2Schema,
    targetAcos: AiPolicySourceV2Schema,
    approvalRiskThreshold: AiPolicySourceV2Schema,
    riskPreference: AiPolicySourceV2Schema,
    bidChangeRiskThresholds: AiPolicySourceV2Schema,
    dryRun: AiPolicySourceV2Schema,
  })
  .strict();
export type AiPolicySourceMapV2 = z.infer<typeof AiPolicySourceMapV2Schema>;

export const EffectiveAiPolicyV2Schema = AiPolicyFieldsV1Schema.extend({
  version: z.literal(2),
  management: AiPolicyManagementV2Schema,
  gates: AiPolicyRunGatesV2Schema,
  enrollment: z
    .object({
      systemEnabled: z.boolean(),
      campaignEnabled: z.boolean(),
      managedGroupEnabled: z.boolean().nullable(),
      effectiveEnabled: z.boolean(),
    })
    .strict(),
  sources: AiPolicySourceMapV2Schema,
}).strict();
export type EffectiveAiPolicyV2 = z.infer<typeof EffectiveAiPolicyV2Schema>;

/** New snapshots use V2; persisted V1 snapshots remain readable and executable. */
export const EffectiveAiPolicySchema = z.discriminatedUnion("version", [
  EffectiveAiPolicyV1Schema,
  EffectiveAiPolicyV2Schema,
]);
export type EffectiveAiPolicy = z.infer<typeof EffectiveAiPolicySchema>;

export const AiPolicyDecisionV2Schema = z
  .object({
    version: z.literal(2),
    targetPolicyKind: z.enum(["keyword", "auto", "unsupported"]),
    effectiveThreshold: ApprovalRiskThresholdV1Schema,
    dryRun: z.boolean(),
    sourceSummary: AiPolicySourceMapV2Schema,
    risk: z.enum(["low", "medium", "high", "critical"]),
    proposalRisk: z.enum(["low", "medium", "high", "critical"]).nullable(),
    validatorVerdict: z.enum(["executable", "recommendation", "blocked", "unsupported"]),
    reasonCodes: z.array(z.string().min(1).max(100)).max(20),
  })
  .strict();
export type AiPolicyDecisionV2 = z.infer<typeof AiPolicyDecisionV2Schema>;

export const AiPolicyDecisionSchema = z.discriminatedUnion("version", [
  AiPolicyDecisionV1Schema,
  AiPolicyDecisionV2Schema,
]);
export type AiPolicyDecision = z.infer<typeof AiPolicyDecisionSchema>;

export const SystemAiPolicyResponseV1Schema = z
  .object({
    version: AiPolicyVersionV1Schema,
    systemEnabled: z.boolean(),
    policy: AiPolicyFieldsV1Schema,
    sources: z
      .object({
        systemEnabled: z.enum(["system", "default"]),
        policy: z
          .object({
            businessGoal: z.enum(["system", "default"]),
            targetAcos: z.enum(["system", "default"]),
            approvalRiskThreshold: z.enum(["system", "default"]),
            riskPreference: z.enum(["system", "default"]),
            bidChangeRiskThresholds: z.enum(["system", "default"]),
            dryRun: z.enum(["system", "default"]),
          })
          .strict(),
      })
      .strict(),
    scope: z
      .object({
        kind: z.literal("workspace"),
        workspaceId: z.string().uuid(),
      })
      .strict(),
    modifiedByUserId: z.string().uuid().nullable(),
    modifiedAt: z.string().datetime().nullable(),
    managedGroupResumeImpact: z
      .object({
        groupCount: z.number().int().nonnegative(),
        campaignCount: z.number().int().nonnegative(),
      })
      .strict()
      .default({ groupCount: 0, campaignCount: 0 }),
  })
  .strict();
export type SystemAiPolicyResponseV1 = z.infer<typeof SystemAiPolicyResponseV1Schema>;

export const SystemAiPolicyPatchV1Schema = z
  .object({
    version: AiPolicyVersionV1Schema,
    systemEnabled: z.boolean().optional(),
    policy: AiPolicyFieldsV1Schema.partial().strict().optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.systemEnabled !== undefined ||
      (value.policy !== undefined && Object.keys(value.policy).length > 0),
    { message: "at least one System AI policy field is required" },
  );
export type SystemAiPolicyPatchV1 = z.infer<typeof SystemAiPolicyPatchV1Schema>;

const CampaignAiPolicyOverridePatchV1Schema = z
  .object({
    businessGoal: campaignLeafOverrideV1Schema(BusinessGoalV1Schema).optional(),
    targetAcos: campaignLeafOverrideV1Schema(z.number().finite().min(0.01).max(2)).optional(),
    approvalRiskThreshold: campaignLeafOverrideV1Schema(ApprovalRiskThresholdV1Schema).optional(),
    riskPreference: campaignLeafOverrideV1Schema(RiskPreferenceV1Schema).optional(),
    bidChangeRiskThresholds: campaignLeafOverrideV1Schema(
      BidChangeRiskThresholdsV1Schema,
    ).optional(),
    dryRun: campaignLeafOverrideV1Schema(z.boolean()).optional(),
  })
  .strict();

export const CampaignAiSetupResponseV1Schema = z
  .object({
    version: AiPolicyVersionV1Schema,
    systemPolicy: SystemAiPolicyV1Schema,
    raw: z
      .object({
        campaignEnabled: z.boolean(),
        campaignOverrides: CampaignAiPolicyOverrideV1Schema,
      })
      .strict(),
    effective: EffectiveAiPolicyV1Schema,
    modifiedByUserId: z.string().uuid().nullable(),
    modifiedAt: z.string().datetime().nullable(),
  })
  .strict();
export type CampaignAiSetupResponseV1 = z.infer<typeof CampaignAiSetupResponseV1Schema>;

export const CampaignAiSetupPatchV1Schema = z
  .object({
    version: AiPolicyVersionV1Schema,
    campaignEnabled: z.boolean().optional(),
    overrides: CampaignAiPolicyOverridePatchV1Schema.optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.campaignEnabled !== undefined ||
      (value.overrides !== undefined && Object.keys(value.overrides).length > 0),
    { message: "at least one Campaign AI policy field is required" },
  );
export type CampaignAiSetupPatchV1 = z.infer<typeof CampaignAiSetupPatchV1Schema>;

export const CampaignAiPolicyEnrollmentInputV1Schema = z
  .object({ campaignEnabled: z.boolean() })
  .strict();
export type CampaignAiPolicyEnrollmentInputV1 = z.infer<
  typeof CampaignAiPolicyEnrollmentInputV1Schema
>;

const DEFAULT_BID_CHANGE_RISK_THRESHOLDS_V1 = Object.freeze({
  medium: 0.05,
  high: 0.15,
  critical: 0.4,
  weeklyCritical: 0.5,
});

export const FAIL_CLOSED_AI_POLICY_DEFAULTS_V1: Readonly<SystemAiPolicyV1> = Object.freeze({
  version: 1,
  systemEnabled: false,
  businessGoal: "improve_efficiency",
  targetAcos: 0.3,
  approvalRiskThreshold: "all",
  riskPreference: "balanced",
  bidChangeRiskThresholds: DEFAULT_BID_CHANGE_RISK_THRESHOLDS_V1,
  dryRun: true,
});

export const FOLLOW_SYSTEM_AI_POLICY_OVERRIDES_V1: Readonly<CampaignAiPolicyOverrideV1> =
  Object.freeze({
    businessGoal: Object.freeze({ mode: "follow_system" }),
    targetAcos: Object.freeze({ mode: "follow_system" }),
    approvalRiskThreshold: Object.freeze({ mode: "follow_system" }),
    riskPreference: Object.freeze({ mode: "follow_system" }),
    bidChangeRiskThresholds: Object.freeze({ mode: "follow_system" }),
    dryRun: Object.freeze({ mode: "follow_system" }),
  });

export const DEFAULT_SYSTEM_AI_POLICY_DOCUMENT_V1 = SystemAiPolicyDocumentV1Schema.parse({
  version: 1,
  policy: {
    businessGoal: FAIL_CLOSED_AI_POLICY_DEFAULTS_V1.businessGoal,
    targetAcos: FAIL_CLOSED_AI_POLICY_DEFAULTS_V1.targetAcos,
    approvalRiskThreshold: FAIL_CLOSED_AI_POLICY_DEFAULTS_V1.approvalRiskThreshold,
    riskPreference: FAIL_CLOSED_AI_POLICY_DEFAULTS_V1.riskPreference,
    bidChangeRiskThresholds: FAIL_CLOSED_AI_POLICY_DEFAULTS_V1.bidChangeRiskThresholds,
    dryRun: FAIL_CLOSED_AI_POLICY_DEFAULTS_V1.dryRun,
  },
});
export const DEFAULT_CAMPAIGN_AI_POLICY_DOCUMENT_V1 = CampaignAiPolicyDocumentV1Schema.parse({
  version: 1,
  overrides: FOLLOW_SYSTEM_AI_POLICY_OVERRIDES_V1,
});

type PolicyField = keyof AiPolicyFieldsV1;

function resolveLeaf<K extends PolicyField>(
  key: K,
  systemPolicy: SystemAiPolicyV1,
  systemSource: "system" | "default",
  overrides: CampaignAiPolicyOverrideV1,
): { value: AiPolicyFieldsV1[K]; source: AiPolicySourceV1 } {
  const override = overrides[key] as CampaignLeafOverrideV1<AiPolicyFieldsV1[K]>;
  if (override.mode === "override") {
    return { value: override.value, source: "campaign" };
  }
  return { value: systemPolicy[key], source: systemSource };
}

/**
 * Resolves one complete V1 policy without I/O.
 *
 * Missing or malformed System policy uses the canonical fail-closed defaults.
 * Malformed Campaign overrides or enrollment disable the Campaign and ignore
 * every Campaign override so a partially trusted row cannot become executable.
 */
export function resolveAiPolicyV1(
  defaults: SystemAiPolicyV1,
  system: unknown | null | undefined,
  campaignOverride: unknown,
  enrollment: unknown,
): EffectiveAiPolicyV1 {
  const parsedDefaults = SystemAiPolicyV1Schema.parse(defaults);
  const parsedSystem = SystemAiPolicyV1Schema.safeParse(system);
  const systemPolicy = parsedSystem.success ? parsedSystem.data : parsedDefaults;
  const systemSource = parsedSystem.success ? "system" : "default";

  const parsedCampaign = CampaignAiPolicyOverrideV1Schema.safeParse(campaignOverride);
  const parsedEnrollment = CampaignAiPolicyEnrollmentInputV1Schema.safeParse(enrollment);
  const campaignIsValid = parsedCampaign.success && parsedEnrollment.success;
  const overrides = campaignIsValid
    ? parsedCampaign.data
    : CampaignAiPolicyOverrideV1Schema.parse(FOLLOW_SYSTEM_AI_POLICY_OVERRIDES_V1);
  const campaignEnabled = campaignIsValid ? parsedEnrollment.data.campaignEnabled : false;

  const businessGoal = resolveLeaf("businessGoal", systemPolicy, systemSource, overrides);
  const targetAcos = resolveLeaf("targetAcos", systemPolicy, systemSource, overrides);
  const approvalRiskThreshold = resolveLeaf(
    "approvalRiskThreshold",
    systemPolicy,
    systemSource,
    overrides,
  );
  const riskPreference = resolveLeaf("riskPreference", systemPolicy, systemSource, overrides);
  const bidChangeRiskThresholds = resolveLeaf(
    "bidChangeRiskThresholds",
    systemPolicy,
    systemSource,
    overrides,
  );
  const dryRun = resolveLeaf("dryRun", systemPolicy, systemSource, overrides);

  return EffectiveAiPolicyV1Schema.parse({
    version: 1,
    enrollment: {
      systemEnabled: systemPolicy.systemEnabled,
      campaignEnabled,
      effectiveEnabled: systemPolicy.systemEnabled && campaignEnabled,
    },
    businessGoal: businessGoal.value,
    targetAcos: targetAcos.value,
    approvalRiskThreshold: approvalRiskThreshold.value,
    riskPreference: riskPreference.value,
    bidChangeRiskThresholds: bidChangeRiskThresholds.value,
    dryRun: dryRun.value,
    sources: {
      enrollment: {
        systemEnabled: systemSource,
        campaignEnabled: "campaign",
      },
      businessGoal: businessGoal.source,
      targetAcos: targetAcos.source,
      approvalRiskThreshold: approvalRiskThreshold.source,
      riskPreference: riskPreference.source,
      bidChangeRiskThresholds: bidChangeRiskThresholds.source,
      dryRun: dryRun.source,
    },
  });
}

export const RiskLevelV1Schema = z.enum(["low", "medium", "high", "critical"]);
export type RiskLevelV1 = z.infer<typeof RiskLevelV1Schema>;

const RISK_RANK: Record<RiskLevelV1, number> = {
  low: 0,
  medium: 1,
  high: 2,
  critical: 3,
};

const APPROVAL_MINIMUM_RISK: Record<ApprovalRiskThresholdV1, number> = {
  all: 0,
  medium_plus: 1,
  high_plus: 2,
  critical_only: 3,
  none: Number.POSITIVE_INFINITY,
};

export function shouldActionRequireApproval(
  actionRisk: RiskLevelV1,
  threshold: ApprovalRiskThresholdV1,
): boolean {
  return RISK_RANK[actionRisk] >= APPROVAL_MINIMUM_RISK[threshold];
}

export function highestRiskLevel(risks: readonly RiskLevelV1[]): RiskLevelV1 | null {
  return risks.reduce<RiskLevelV1 | null>(
    (highest, risk) => (highest === null || RISK_RANK[risk] > RISK_RANK[highest] ? risk : highest),
    null,
  );
}
